/**
 * services/RetiredDraftLineageService.ts
 * Service for managing "Delete Forever" carry-forward lineage retirement records.
 *
 * A retired lineage never surfaces again as a carry-forward candidate in any
 * future Pre-Trading session. Retirement is durable, synced across devices,
 * and keyed by rootDraftId (not by individual draft node).
 *
 * @see RetiredDraftLineage — schema type
 * @see SessionDraftService.getPendingCarryForwardDrafts — exclusion consumer
 */

// 1. External dependencies
// (none)

// 2. Internal absolute imports
import { BaseService } from './BaseService';
import { db } from '@/lib/db';
import { generateUUID } from '@/utils/uuid';
import { syncCoordinatorService } from './sync/SyncCoordinatorService';
import { getSyncOperationService } from './sync/SyncOperationService';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { buildSetOps } from './sync/OperationBuilder';

// 3. Type imports
import type { RetiredDraftLineage } from '@/types/schema/RetiredDraftLineage';

// ===== SERVICE =====

class RetiredDraftLineageService extends BaseService<RetiredDraftLineage> {
  constructor() {
    super('retired_draft_lineages');
  }

  /**
   * Permanently retire a carry-forward lineage by rootDraftId.
   *
   * Idempotent — if the lineage is already retired, this is a no-op.
   * Race condition safe — if two near-simultaneous calls both pass the pre-check,
   * the unique `&rootDraftId` index rejects the second write with a ConstraintError,
   * which is caught and treated as an already-retired state (silent return).
   *
   * @param rootDraftId - The rootDraftId of the lineage to retire
   * @param retiredBySessionDraftId - The current session draft ID (audit context)
   */
  async retire(rootDraftId: string, retiredBySessionDraftId: string): Promise<void> {
    // Guard: rootDraftId must be a non-empty string — pre-WU2A drafts may lack this field
    if (!rootDraftId) throw new Error(`retire() called with invalid rootDraftId: ${String(rootDraftId)}`);

    // Pre-check: already retired — no-op
    const existing = await this.table.where('rootDraftId').equals(rootDraftId).first();
    if (existing !== undefined) return;

    const now = new Date().toISOString();
    const entity: RetiredDraftLineage = {
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
      rootDraftId,
      retiredAt: now,
      retiredBySessionDraftId,
    };

    // Bypass BaseService.add() — it wraps all errors as a plain Error, which would
    // swallow the Dexie ConstraintError name needed for race-condition detection.
    // Raw write inside a manual transaction preserves the original ConstraintError.
    try {
      await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
        markAsInstrumentedWriteContext();
        await this.table.add(entity);
        const ops = buildSetOps(
          'retired_draft_lineages',
          entity.id,
          entity as unknown as Record<string, unknown>,
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      });
    } catch (error) {
      // ConstraintError: race condition — another call wrote between our pre-check and add()
      // The lineage is now retired regardless, so return silently.
      if ((error as { name?: string }).name === 'ConstraintError') return;
      throw error;
    }

    syncCoordinatorService.notifyChange('retired_draft_lineages', entity.id);
  }

  /**
   * Check whether a lineage has been permanently retired.
   *
   * @param rootDraftId - The rootDraftId to check
   * @returns true if a retirement record exists (including tombstoned records)
   */
  async isRetired(rootDraftId: string): Promise<boolean> {
    const record = await this.table.where('rootDraftId').equals(rootDraftId).first();
    return record !== undefined;
  }

  /**
   * Get the set of all retired rootDraftIds.
   *
   * Uses raw `toArray()` intentionally — tombstoned retirement records still count
   * as retired. A soft-deleted retirement record must NOT un-retire a lineage.
   *
   * @returns Set of rootDraftId strings for all retired lineages
   */
  async getRetiredRootDraftIds(): Promise<Set<string>> {
    const records = await this.table.toArray();
    return new Set(records.map((r) => r.rootDraftId));
  }
}

/** Singleton instance */
export const retiredDraftLineageService = new RetiredDraftLineageService();
